// Age and life-stage rules. These drive meal counts, toilet intervals, nap
// windows and exercise guidance across the app, so they live in one place.

import { daysBetween, parseISODate, today } from './time.ts';
import type { ISODate, Millis, Profile } from './types.ts';

export interface Age {
  days: number;
  weeks: number;
  /** Whole calendar months. */
  months: number;
  label: string;
}

export function ageOn(dob: ISODate, on: ISODate): Age {
  const days = Math.max(0, daysBetween(dob, on));
  const weeks = Math.floor(days / 7);
  const b = parseISODate(dob);
  const d = parseISODate(on);
  let months = (d.getFullYear() - b.getFullYear()) * 12 + (d.getMonth() - b.getMonth());
  if (d.getDate() < b.getDate()) months -= 1;
  months = Math.max(0, months);
  let label: string;
  if (weeks < 16) {
    const extra = days % 7;
    label = extra ? `${weeks} weeks ${extra} day${extra > 1 ? 's' : ''}` : `${weeks} weeks`;
  } else if (months < 24) label = `${months} months`;
  else label = `${Math.floor(months / 12)} years`;
  return { days, weeks, months, label };
}

export function ageNow(profile: Pick<Profile, 'dob'>, now: Millis = Date.now()): Age | undefined {
  return profile.dob ? ageOn(profile.dob, today(now)) : undefined;
}

export type Stage = 'newborn' | 'baby' | 'puppy' | 'junior' | 'adolescent' | 'adult';

export interface StageInfo {
  stage: Stage;
  title: string;
  summary: string;
}

export function stageFor(ageWeeks: number): StageInfo {
  if (ageWeeks < 8)
    return { stage: 'newborn', title: 'With mum', summary: 'Still with mum and littermates, learning to be a dog.' };
  if (ageWeeks < 16)
    return {
      stage: 'baby',
      title: 'Baby puppy',
      summary: 'Socialisation window is open. Lots of sleep, frequent toilet trips, gentle new experiences every day.',
    };
  if (ageWeeks < 26)
    return {
      stage: 'puppy',
      title: 'Puppy',
      summary: 'Teething (adult teeth arrive 3–6 months). Keep up socialisation and short, fun training.',
    };
  if (ageWeeks < 39)
    return {
      stage: 'junior',
      title: 'Junior',
      summary: 'Confidence growing, adolescence starting. Keep rules consistent and recall on a long line.',
    };
  if (ageWeeks < 78)
    return {
      stage: 'adolescent',
      title: 'Teenager',
      summary: 'Adolescence peaks around 8 months: "selective hearing", a recall wobble, more worry about being alone. Patience, consistency, lots of sniffing.',
    };
  return { stage: 'adult', title: 'Adult', summary: 'All grown up. Cockers reach adult height by 9–12 months, then fill out — keep an eye on waistline.' };
}

/** Meals per day by age (UK charity guidance). */
export function mealsPerDay(ageWeeks: number): 2 | 3 | 4 {
  if (ageWeeks < 12) return 4;
  if (ageWeeks < 26) return 3;
  return 2;
}

/**
 * Typical maximum awake-and-busy time between toilet trips, in minutes.
 * The Royal Kennel Club says young puppies need to wee "at least every hour
 * or two", plus straight after waking, eating, playing and excitement. The
 * popular "(age in months + 1) hours" is a rough upper bound, not a target.
 */
export function toiletIntervalMinutes(ageWeeks: number): number {
  if (ageWeeks < 12) return 60;
  if (ageWeeks < 16) return 90;
  if (ageWeeks < 26) return 150;
  if (ageWeeks < 52) return 240;
  return 300;
}

/**
 * When to tell you he's been on his own a while. Dogs Trust and the RSPCA
 * advise building alone time up gradually from a few minutes; young puppies
 * also need a toilet trip roughly every `toiletIntervalMinutes`. This is a
 * nudge, not a rule — adults shouldn't be left more than about 4 hours.
 */
export function aloneAlertMinutes(ageWeeks: number): number {
  if (ageWeeks < 12) return 45;
  if (ageWeeks < 16) return 75;
  if (ageWeeks < 26) return 120;
  if (ageWeeks < 52) return 180;
  return 240;
}

/** How long a puppy can comfortably be awake before an overtired wobble. */
export function awakeWindowMinutes(ageWeeks: number): number {
  if (ageWeeks < 12) return 60;
  if (ageWeeks < 16) return 75;
  if (ageWeeks < 26) return 105;
  if (ageWeeks < 52) return 150;
  return 240;
}

/** Recommended hours of sleep in 24h. */
export function sleepTargetHours(ageWeeks: number): [number, number] {
  if (ageWeeks < 16) return [18, 20];
  if (ageWeeks < 52) return [16, 18];
  return [12, 14];
}

/**
 * Structured on-lead walk guidance in minutes per outing. The Royal Kennel
 * Club still suggests "5 minutes of formal exercise per month of age, up to
 * twice a day"; PDSA points out there is no evidence behind it. We show it as
 * a gentle ceiling for *formal* walks — sniffing, free play on soft ground and
 * brain games are separate and encouraged.
 */
export function walkGuideMinutes(ageMonths: number): number {
  return Math.min(60, Math.max(5, ageMonths * 5));
}

/**
 * Rough growth band (kg) for a UK English cocker *male* heading for ~14–16 kg.
 * There is no authoritative UK chart for cockers; this is estimated from
 * medium-breed growth proportions (see docs/RESEARCH.md). It only shades the
 * weight chart — the trend matters far more than the absolute number.
 */
export const COCKER_GROWTH: { weeks: number; low: number; high: number }[] = [
  { weeks: 8, low: 2.5, high: 4 },
  { weeks: 12, low: 4, high: 6 },
  { weeks: 16, low: 6, high: 8 },
  { weeks: 26, low: 9, high: 12 },
  { weeks: 39, low: 12, high: 14 },
  { weeks: 52, low: 13, high: 15.5 },
  { weeks: 78, low: 13, high: 16.5 },
];

export function growthBand(ageWeeks: number): { low: number; high: number } | undefined {
  const pts = COCKER_GROWTH;
  if (ageWeeks < pts[0].weeks || ageWeeks > pts[pts.length - 1].weeks) return undefined;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (ageWeeks >= a.weeks && ageWeeks <= b.weeks) {
      const f = (ageWeeks - a.weeks) / (b.weeks - a.weeks);
      return { low: a.low + (b.low - a.low) * f, high: a.high + (b.high - a.high) * f };
    }
  }
  return undefined;
}
